import { CreateTagDocument, TagsDocument } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for creating a tag from the picker.
 *
 * The backend derives the tag's immutable slug id from `name` and rejects duplicates, so the
 * only client-side requirement is a non-empty name. The new tag is appended to the cached
 * active-tag list so every open picker shows it without a refetch.
 *
 * @returns An object containing:
 *   - createTag: Async function taking a name and hex color, returns the created tag.
 *   - loading: Boolean indicating if the mutation is in progress
 */
export function useCreateTag() {
  const [createTagMutation, { loading }] = useMutation(CreateTagDocument, {
    update(cache, { data }) {
      if (!data?.createTag) return;

      cache.updateQuery({ query: TagsDocument, variables: { isArchived: false } }, (existing) =>
        existing ? { tags: [...(existing.tags ?? []), data.createTag] } : existing
      );
    },
  });

  const createTag = async (name: string, color: string) => {
    return withToast(
      async () => {
        const result = await createTagMutation({ variables: { name, color } });
        return result.data?.createTag;
      },
      {
        loading: "Creating tag...",
        success: `Created "${name.trim()}"`,
        // Surfaces the server's reason, which is how the editor learns the name is taken.
        error: (error) =>
          error instanceof Error ? error.message : "Failed to create tag. Please try again.",
      },
      "createTag"
    );
  };

  return {
    createTag,
    loading,
  };
}
